/**
 * <s-variant-media> — Brings the selected variant's image to the front of the product gallery.
 *
 * Rendered inside the product-form partial, so a fresh element connects every time a
 * shopper picks a different option (for example "Order a Sample"). On connect it moves
 * the matching [data-media-id] item to the start of [data-product-gallery]. When the
 * variant has no image of its own, the gallery returns to its original order.
 *
 * Attributes:
 *   data-media-id — Media ID of the selected variant's featured image (may be empty).
 */
class SVariantMedia extends HTMLElement {
  connectedCallback() {
    const gallery = document.querySelector('[data-product-gallery]');
    if (!gallery) return;

    const isInitial = !gallery.hasAttribute('data-variant-media-ready');
    gallery.setAttribute('data-variant-media-ready', '');

    const items = Array.from(gallery.querySelectorAll(':scope > [data-media-id]'));
    if (items.length < 2) return;

    const mediaId = this.dataset.mediaId || '';
    const target = mediaId ? items.find((item) => item.dataset.mediaId === mediaId) : null;

    // Original order, then the selected variant's image first when it has one.
    const ordered = items
      .slice()
      .sort((a, b) => Number(a.dataset.mediaIndex) - Number(b.dataset.mediaIndex));
    if (target) {
      ordered.splice(ordered.indexOf(target), 1);
      ordered.unshift(target);
    }

    const unchanged = ordered.every((item, index) => items[index] === item);
    if (unchanged) return;

    ordered.forEach((item) => gallery.appendChild(item));

    if (isInitial) return;

    // Mobile carousel: return to the first slide. Desktop grid: bring the gallery into view if it's scrolled away.
    gallery.scrollTo({ left: 0, behavior: 'instant' });
    const top = gallery.getBoundingClientRect().top;
    if (top < 0) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      gallery.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'instant' : 'smooth' });
    }
  }
}

if (!customElements.get('s-variant-media')) {
  customElements.define('s-variant-media', SVariantMedia);
}
